import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useMemo, useState } from 'react';
import { logError } from 'commons/logger';
import PostHogFeatureFlagsContext from './PostHogFeatureFlagsContext';
import { getAppPostHogClient } from './appPostHogClient';
const postHogOverrideState = {
  hasOverridden: false
};
const reportedExposures = new Set();

// The app client starts after LCP, up to 8s plus idle time when LCP is unobservable
const POSTHOG_INIT_MAX_WAIT_MS = 30_000;
const POSTHOG_INIT_POLL_MS = 100;
const getCurrentViewKey = () => `${window.location.pathname}${window.location.search}`;

// `clear` and bad input both return false, which drops any overrides.
// `error` is set on bad input, for the caller that logs it.
const parsePostHogQueryQuietly = query => {
  try {
    const queryObject = JSON.parse(query);
    if (queryObject?.clear === true) {
      return {
        value: false
      };
    }
    return {
      value: queryObject
    };
  } catch (err) {
    return {
      value: false,
      error: err
    };
  }
};

// Only for the effect. Logging during render would hit Sentry on every
// render, and twice in StrictMode.
const parsePostHogQuery = query => {
  const {
    value,
    error
  } = parsePostHogQueryQuietly(query);
  if (error) {
    logError(error, 'Unable to parse posthogOverrides query', {
      query
    });
  }
  return value;
};
const applyPostHogOverrides = posthog => {
  const postHogOverrideQuery = new URLSearchParams(window.location.search).get('posthogOverrides');
  if (postHogOverrideState.hasOverridden || !postHogOverrideQuery || postHogOverrideQuery === '' || postHogOverrideQuery === '{}' || !posthog?.featureFlags) {
    return;
  }
  const parsedQuery = parsePostHogQuery(postHogOverrideQuery);
  if (parsedQuery !== false) {
    postHogOverrideState.hasOverridden = true;
    posthog.featureFlags.override(parsedQuery);
  }
};

// ?posthogOverrides wins over the server value. Otherwise the page shows one
// variant and reports the other.
// Skips the hasOverridden latch on purpose: that guards a once-per-session
// mutation, while this read must return the same value on every render.
const readOverrideValue = (name, search) => {
  const query = new URLSearchParams(search).get('posthogOverrides');
  if (!query || query === '' || query === '{}') {
    return undefined;
  }
  const {
    value
  } = parsePostHogQueryQuietly(query);
  return value === false ? undefined : value?.[name];
};
const readLiveFlagState = (posthog, name) => {
  if (!posthog?.isFeatureEnabled(name)) {
    return {
      value: null,
      payload: null
    };
  }
  const val = posthog.getFeatureFlag(name);
  const payloadValue = posthog.getFeatureFlagPayload(name);
  if (val === null) {
    return {
      value: null,
      payload: null
    };
  }
  return {
    value: val,
    payload: payloadValue
  };
};
const buildExposureKey = (posthog, name, distinctId) => {
  const resolvedDistinctId = distinctId || posthog?.get_distinct_id?.() || 'unknown-distinct-id';
  return `${name}::${resolvedDistinctId}::${getCurrentViewKey()}`;
};
const reportFeatureFlagExposure = (posthog, name, distinctId) => {
  if (!posthog) {
    return;
  }
  const exposureKey = buildExposureKey(posthog, name, distinctId);
  if (reportedExposures.has(exposureKey)) {
    return;
  }
  reportedExposures.add(exposureKey);
  posthog.getFeatureFlag(name);
};

// Not window.posthog: RudderStack's instance can hold it without the seed
const waitForPostHog = onReady => {
  if (getAppPostHogClient()) {
    onReady(getAppPostHogClient());
    return () => {};
  }
  const startedAt = Date.now();
  const intervalId = setInterval(() => {
    if (getAppPostHogClient()) {
      clearInterval(intervalId);
      onReady(getAppPostHogClient());
      return;
    }
    if (Date.now() - startedAt >= POSTHOG_INIT_MAX_WAIT_MS) {
      clearInterval(intervalId);
    }
  }, POSTHOG_INIT_POLL_MS);
  return () => {
    clearInterval(intervalId);
  };
};
const usePostHogFeatureFlagWithPayload = name => {
  const $ = _c(26);
  const seededContext = useContext(PostHogFeatureFlagsContext);
  const seededValue = seededContext?.featureFlags?.[name];
  const seededDistinctId = seededContext?.distinctId;
  let t0;
  if ($[0] !== name || $[1] !== seededContext?.featureFlags) {
    let t1;
    if ($[3] !== seededContext?.featureFlags) {
      t1 = seededContext?.featureFlags || {};
      $[3] = seededContext?.featureFlags;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = Object.prototype.hasOwnProperty.call(t1, name);
    $[0] = name;
    $[1] = seededContext?.featureFlags;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const hasSeededValue = t0;
  const search = typeof window === "undefined" ? "" : window.location.search;
  let t1;
  if ($[5] !== name) {
    t1 = readOverrideValue(name, search);
    $[5] = name;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const overrideValue = t1;
  const seedValueToUse = overrideValue === undefined ? seededValue : overrideValue;
  let t2;
  if ($[7] !== hasSeededValue || $[8] !== seededValue) {
    t2 = () => hasSeededValue ? seededValue : null;
    $[7] = hasSeededValue;
    $[8] = seededValue;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const [value, setValue] = useState(t2);
  const [payload, setPayload] = useState(null);
  let t3;
  let t4;
  if ($[10] !== hasSeededValue || $[11] !== seedValueToUse) {
    t3 = () => {
      if (hasSeededValue) {
        setValue(seedValueToUse);
      }
    };
    t4 = [hasSeededValue, seedValueToUse];
    $[10] = hasSeededValue;
    $[11] = seedValueToUse;
    $[12] = t3;
    $[13] = t4;
  } else {
    t3 = $[12];
    t4 = $[13];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[14] !== hasSeededValue || $[15] !== name) {
    t5 = () => {
      if (!name || hasSeededValue) {
        return;
      }
      const removePostHogEventListeners = [];
      const subscribeToPostHog = posthog => {
        applyPostHogOverrides(posthog);
        const removePostHogEventListener = posthog.onFeatureFlags(() => {
          applyPostHogOverrides(posthog);
          const nextState = readLiveFlagState(posthog, name);
          if (nextState.value !== null) {
            setValue(nextState.value);
            setPayload(nextState.payload);
          }
        });
        removePostHogEventListeners.push(removePostHogEventListener);
        const initialState = readLiveFlagState(posthog, name);
        if (initialState.value !== null) {
          setValue(initialState.value);
          setPayload(initialState.payload);
        }
      };
      const cancelWait = waitForPostHog(subscribeToPostHog);
      return () => {
        cancelWait();
        removePostHogEventListeners.forEach(_temp);
      };
    };
    t6 = [hasSeededValue, name];
    $[14] = hasSeededValue;
    $[15] = name;
    $[16] = t5;
    $[17] = t6;
  } else {
    t5 = $[16];
    t6 = $[17];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[18] !== hasSeededValue || $[19] !== name || $[20] !== seededDistinctId) {
    t7 = () => {
      if (!name || !hasSeededValue || typeof window === "undefined") {
        return;
      }
      const reportExposure = posthog_0 => {
        applyPostHogOverrides(posthog_0);
        reportFeatureFlagExposure(posthog_0, name, seededDistinctId);
      };
      return waitForPostHog(reportExposure);
    };
    t8 = [hasSeededValue, name, seededDistinctId];
    $[18] = hasSeededValue;
    $[19] = name;
    $[20] = seededDistinctId;
    $[21] = t7;
    $[22] = t8;
  } else {
    t7 = $[21];
    t8 = $[22];
  }
  useEffect(t7, t8);
  let t9;
  if ($[23] !== payload || $[24] !== value) {
    t9 = [value, payload];
    $[23] = payload;
    $[24] = value;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
};
export const resetPostHogFeatureFlagTestState = () => {
  postHogOverrideState.hasOverridden = false;
  reportedExposures.clear();
};
export default usePostHogFeatureFlagWithPayload;
function _temp(removePostHogEventListener_0) {
  removePostHogEventListener_0?.();
}